import React, { useContext } from "react";
import styled from "styled-components";

import LocaleContext from "@/i18n";
import { Icons, IThemeProps, Svg } from "@/styles";

type FormRadioProps = {
  name: string;
  label: string;
  onChange: (value: string) => void;
  options: string[];
  currentValue?: string;
  valid?: boolean;
};

const FormRadio: React.FC<FormRadioProps> = ({
  name,
  label,
  onChange,
  options,
  currentValue,
  valid,
}) => {
  const { translation } = useContext(LocaleContext);

  return (
    <fieldset>
      <Legend>{label}</Legend>
      <Row>
        {options.map((option) => (
          <Option key={option}>
            <Input
              data-qa={`input.radio.${option}`}
              id={option}
              name={name}
              value={option}
              checked={currentValue === option}
              onChange={(e) => onChange(e.target.value)}
            />
            <Label htmlFor={option}>
              {(translation.registration as any)[option] ?? option}
            </Label>
          </Option>
        ))}
        {!valid && (
          <Status>
            <Svg contents={Icons.Warning} height={18} width={18} />
          </Status>
        )}
      </Row>
    </fieldset>
  );
};

export default FormRadio;

const Legend = styled.legend<IThemeProps>`
  margin-bottom: 4px;
  text-transform: uppercase;
  font-size: ${(props) => props.theme.typography.h100};
  color: ${(props) =>
    props.theme.colors.inputColor || props.theme.colors.primary};
`;

const Row = styled.div<IThemeProps>`
  display: flex;
  /* ESD-2418 §3.7: wrap the salutation options (now four incl. "Keine Angabe") so they never
     overflow / cause horizontal scroll on a 360px viewport. */
  flex-wrap: wrap;
  row-gap: 8px;
`;

const Option = styled.div<IThemeProps>`
  margin-right: 20px;
`;

const Label = styled.label<IThemeProps>`
  cursor: pointer;
  /* ESD-2418 §3.7: the label IS the clickable element (htmlFor toggles the radio) — make it a real
     ≥44px touch target, with the circle + text vertically centred. */
  display: flex;
  align-items: center;
  min-height: 44px;

  &:before {
    content: "";
    flex: none;
    display: inline-block;
    margin-right: 10px;
    border: 5px solid ${(props) => props.theme.colors.inputBackground};
    background-color: ${(props) => props.theme.colors.inputBackground};
    border-radius: 50%;
    width: 20px;
    height: 20px;
    vertical-align: middle;
    transition: border-color 0.2s;
  }
`;

const Input = styled.input.attrs<IThemeProps>({ type: "radio" })`
  appearance: none;
  position: absolute;
  left: -10px;
  top: -8px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  opacity: 0;

  &:checked + label:before {
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const Status = styled.span<IThemeProps>`
  svg {
    fill: ${(props) => props.theme.colors.error};
  }
`;
